/**
 * How numbers and times read on screen. Nothing here touches the network or
 * the DOM, so every judgement — when a timestamp stops being useful, where a
 * thousands separator goes — is testable on its own.
 *
 * THIS FILE USED TO BE ABOUT MONEY. It held the denomination (a hundred to the
 * dollar), a balance formatter, a whole-dollar spend limit, a signed ledger
 * amount and a volume rate. Ruling R-0916-1 deleted every one of the things
 * they formatted, and no money figure is composed on this side any more: a
 * price arrives from the server as a rendered string, because the two halves of
 * a disclosed amount have to be arithmetically consistent or the screen is a
 * misrepresentation.
 *
 * What survives is the digit grouping, which was never about money — it counts
 * a percentile and a raw score on an inbox card. It is done by hand rather than
 * by `toLocaleString` so that a test, a server render and every browser agree.
 */

/** `1300` → `"1,300"`. Whole numbers only; a fraction is rounded to one. */
export function formatCount(value: number): string {
  const whole = Math.round(Number.isFinite(value) ? value : 0);
  const digits = Math.abs(whole)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return whole < 0 ? `−${digits}` : digits;
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * A span of saved time, read as a briefing states it: `"3h 42m"`, `"45m"`,
 * `"2h"`. Minutes are dropped when they round to zero, hours when there are
 * none — the Morning Nob says "3h 42m of inbox archaeology", never "3h 0m"
 * (FR-7.3). The server sends its own label; this is the fallback when it does
 * not, computed from `knocks_filtered × 45s`.
 */
export function formatDuration(seconds: number): string {
  const total = Math.max(0, Math.round(Number.isFinite(seconds) ? seconds : 0));
  const minutes = Math.round(total / 60);
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

/** Relative until it stops being useful, then a plain date. Never "0 minutes ago". */
export function relativeTime(iso: string | null, now: number = Date.now()): string {
  if (!iso) return "";
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return "";
  const ms = now - then;
  if (ms < 0) return "just now";
  if (ms < MINUTE) return "just now";
  if (ms < HOUR) {
    const n = Math.floor(ms / MINUTE);
    return n === 1 ? "a minute ago" : `${n} minutes ago`;
  }
  if (ms < DAY) {
    const n = Math.floor(ms / HOUR);
    return n === 1 ? "an hour ago" : `${n} hours ago`;
  }
  if (ms < 2 * DAY) return "yesterday";
  if (ms < 30 * DAY) return `${Math.floor(ms / DAY)} days ago`;
  const date = new Date(then);
  const sameYear = date.getFullYear() === new Date(now).getFullYear();
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    ...(sameYear ? {} : { year: "numeric" }),
  });
}

/**
 * The same moment, in the width a LIST can spare: `"now"`, `"5m"`, `"2h"`,
 * `"3d"`, then `"Jul 30"`. One column of a conversation row is not the place for
 * "3 days ago" — the row is scanned, not read, and the date is the honest answer
 * once the count stops being one.
 *
 * `relativeTime` above is unchanged and still the right thing in prose. This is
 * its terse sibling, for the rooms that show many rows at once.
 */
export function shortWhen(iso: string | null, now: number = Date.now()): string {
  if (!iso) return "";
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return "";
  const ms = now - then;
  // A clock skew of a few seconds is not the future; a row that says "in 3
  // minutes" about something that already happened is worse than saying "now".
  if (ms < MINUTE) return "now";
  if (ms < HOUR) return `${Math.floor(ms / MINUTE)}m`;
  if (ms < DAY) return `${Math.floor(ms / HOUR)}h`;
  if (ms < 7 * DAY) return `${Math.floor(ms / DAY)}d`;
  const date = new Date(then);
  const sameYear = date.getFullYear() === new Date(now).getFullYear();
  // en-US, deliberately: "Jul 30" reads as one glanceable token, and the format
  // must not depend on the machine's locale — a test, a server render and every
  // browser have to agree.
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" }),
  });
}

/**
 * A calendar date as "9 Oct 2026" — day, short month, year — in UTC, so the
 * words do not depend on the machine's locale or zone (a test, a server render
 * and every browser agree). For dates a person acts against, like a lapse or a
 * deletion; `shortWhen` is for rows scanned in a list.
 */
export function formatDate(iso: string | null): string {
  if (!iso) return "";
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "";
  return at.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}
